import { useEffect, useId, useState } from "react";
import { ArrowRight, Check, ClipboardCheck, Clock3, ExternalLink, FileSearch, ShieldCheck, Trash2, TriangleAlert } from "lucide-react";
import { Badge, Button, Dialog, DialogActions, Field, FilterBar, SearchField, Select, Table } from "@studio";
import { MetricGrid } from "../console/metrics";
import { create } from "zustand";
import { CaptureImage, EvidenceViewer, type CaptureAsset } from "@studio-features/meta-crew/evidence-viewer";
import { api, ApiError, errorMessage } from "../lib/api";
import { can } from "../lib/access";
import { errorKindNames, fmtDateTime, fmtTime } from "../lib/format";
import { keys, useAction, useCustomers, useResultReview, useResultReviews, useSettings } from "../lib/queries";
import type { CustomerSummary, Me, PublicationResult, PublishStage, ResultReviewRow, ReviewState, Screenshot, TaskDetail, TenantIntro } from "../lib/types";
import { Empty, ErrorBox, InfoRow, Loading, Panel } from "../console/ui";

/**
 * 게시 결과 검토 (시안 6a084bb publishing.tsx 를 실제 데이터로).
 * 브릿지 5단계 결과(원고 검토·게시·완료 검토·캡처)를 직원이 보고 유지 / 삭제 요청 / 보류를 정한다. 직원 전용.
 */

export const reviewLabels: Record<ReviewState, string> = {
  pending: "검토 대기",
  kept: "유지",
  delete_requested: "삭제 요청",
  deleted: "삭제됨",
  held: "보류",
};
/** 게시 작업의 실패 분류 4종 (시안 failureLabels). 자동 재시도하지 않는다. */
export const failureLabels: Record<string, string> = {
  review_rejected: "검토 반려",
  needs_human: "사람 확인 필요",
  unknown_result: "결과 불명",
  verify_failed: "검토 불일치",
};
const stageLabels = ["원고 검토", "작업 수행", "작업 완료", "완료 검토", "최종 보고"];
const stageKeys: PublishStage[] = ["review", "run", "posted", "verify", "done"];
type Verdict = "일치" | "불일치" | "확인 불가";

/** 화면이 쓰는 게시 결과 한 건. 결과 검토함 행과 작업 상세(TaskDetail)를 같은 모양으로 맞춘다. */
export type Publication = PublicationResult & {
  taskId: string;
  title: string;
  postTitle: string | null;
  taskStatus: string;
  failure: string | null;
  executor: { customerId: string; name: string; blogId?: string | null };
  subject: { customerId: string; name: string };
  crew: boolean;
  machineLabel: string | null;
  deviceSerial: string | null;
  postedAt: string | null;
  resultReview: ResultReviewRow["resultReview"] | null;
  summary: string | null;
};

export function publicationFromRow(r: ResultReviewRow): Publication {
  return {
    ...r,
    taskStatus: r.status,
    failure: r.errorKind && failureLabels[r.errorKind] ? r.errorKind : null,
    machineLabel: r.machine?.label ?? null,
    resultReview: r.resultReview,
  };
}

/** 작업 상세에서: 게시 작업이고 단계·검토 정보가 있을 때만. */
export function publicationFromTask(t: TaskDetail, executor: { customerId: string; name: string }): Publication | null {
  if (t.type !== "naver_blog_post") return null;
  const r = t.result ?? {};
  if (!r.stage && !t.resultReview && !t.postedAt && !(t.errorKind && failureLabels[t.errorKind])) return null;
  return {
    taskId: t.id,
    title: t.title ?? "",
    postTitle: typeof t.params?.title === "string" ? (t.params.title as string) : null,
    taskStatus: t.status,
    failure: t.errorKind && failureLabels[t.errorKind] ? t.errorKind : null,
    executor,
    subject: t.targetCustomer ? { customerId: t.targetCustomer.id, name: t.targetCustomer.name } : executor,
    crew: !!t.crew,
    machineLabel: t.machineLabel ?? null,
    deviceSerial: t.assignedDeviceSerial,
    postedAt: t.postedAt ?? r.posted?.postedAt ?? null,
    resultReview: t.resultReview ?? null,
    summary: r.summary ?? null,
    stage: r.stage ?? null,
    review: r.review ?? null,
    posted: r.posted ?? null,
    verification: r.verification ?? null,
    screenshots: r.screenshots ?? [],
  };
}

export function verdictOf(p: Pick<Publication, "verification" | "review" | "failure" | "taskStatus">): Verdict {
  if (p.verification) return p.verification.verdict === "match" ? "일치" : p.verification.verdict === "mismatch" ? "불일치" : "확인 불가";
  if (p.failure === "review_rejected" || p.review?.verdict === "reject") return "불일치";
  return "확인 불가";
}
const verdictTone = (v: Verdict) => (v === "일치" ? "teal" : "amber");
const reviewTone = (s: ReviewState) => (s === "pending" || s === "held" ? "amber" : s === "delete_requested" ? "blue" : "slate");

/** 실행 이력에서 "결과 검토함 열기"를 누르면 검토함 화면이 이 작업을 바로 연다. */
export const useReviewFocus = create<{ taskId: string | null; focus: (id: string | null) => void }>((set) => ({ taskId: null, focus: (taskId) => set({ taskId }) }));

/* ---------- 부품 ---------- */

export function TenantRoute({ executor, subject, crew }: { executor: { name: string }; subject: { name: string }; crew: boolean }) {
  return (
    <div className="lwp-tenant-route">
      <span>
        <small>실행 고객사</small>
        <strong>{executor.name}</strong>
      </span>
      <ArrowRight size={16} aria-hidden="true" />
      <span>
        <small>대상 고객사</small>
        <strong>{subject.name}</strong>
      </span>
      {crew && <Badge tone="blue">크루 작업</Badge>}
    </div>
  );
}

export function PublishProgress({ stage, failure, succeeded }: { stage: PublishStage | null | undefined; failure: string | null; succeeded: boolean }) {
  // 실패 분류가 가리키는 단계: 검토 반려 → 1, 사람 확인 → 보통 2, 결과 불명 → 3(게시 뒤), 검토 불일치 → 4.
  const failureStage: Record<string, PublishStage> = { review_rejected: "review", needs_human: "run", unknown_result: "posted", verify_failed: "verify" };
  const current = stageKeys.indexOf(succeeded ? "done" : failure ? (stage && stageKeys.indexOf(stage) > stageKeys.indexOf(failureStage[failure]) ? stage : failureStage[failure]) : (stage ?? "review"));
  return (
    <ol className="lwp-stages" aria-label="게시 작업 5단계">
      {stageLabels.map((label, i) => {
        const done = i < current || (succeeded && i === current);
        const failed = !!failure && i === current;
        return (
          <li key={label} className={failed ? "is-warning" : done ? "is-done" : i === current ? "is-current" : ""} aria-current={i === current ? "step" : undefined}>
            <span>{done ? <Check size={14} /> : i + 1}</span>
            <div>
              <strong>{label}</strong>
              <small>{failed ? failureLabels[failure!] : done ? "완료" : i === current ? "진행 중" : "대기"}</small>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** 캡처 이미지(목록 썸네일): 실행 고객사 범위의 파일 API 를 토큰으로 받아 object URL 로 보여준다. */
function ScreenshotImage({ customerId, shot }: { customerId: string; shot: Screenshot }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    let objectUrl: string | null = null;
    api
      .blobUrl(`/c/${customerId}/files/${shot.fileId}/content`)
      .then((u) => {
        if (!alive) return URL.revokeObjectURL(u);
        objectUrl = u;
        setUrl(u);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [customerId, shot.fileId]);
  if (failed || !url) return <CaptureImage asset={{ id: shot.fileId, label: shot.name, src: "", state: failed ? "error" : "loading" }} thumbnail />;
  return <img className="lwe-thumb-img" src={url} alt={shot.name} />;
}

/**
 * 증거 캡처 묶음 → 시안 EvidenceViewer 의 CaptureAsset (c9ee685). 파일마다 object URL 을 받아 두고, 닫히면 해제한다.
 * 404 는 보관 기간(result_screenshot_days)이 지난 작업일 때만 "보관 만료"로, 그 밖에는 "불러오지 못함"으로 본다 (인수인계 4).
 */
function useCaptureAssets(customerId: string, shots: Screenshot[], at: string | null): CaptureAsset[] {
  const settings = useSettings();
  const keepDays = Number(settings.data?.find((s) => s.key === "result_screenshot_days")?.value ?? 90);
  const [loaded, setLoaded] = useState<Record<string, { src: string; state: CaptureAsset["state"] }>>({});
  const ids = shots.map((s) => s.fileId).join(",");
  useEffect(() => {
    let alive = true;
    const urls: string[] = [];
    setLoaded({});
    for (const s of shots) {
      api
        .blobUrl(`/c/${customerId}/files/${s.fileId}/content`)
        .then((u) => {
          if (!alive) return URL.revokeObjectURL(u);
          urls.push(u);
          setLoaded((m) => ({ ...m, [s.fileId]: { src: u, state: "ready" } }));
        })
        .catch((e: unknown) => {
          if (!alive) return;
          const old = !!at && Date.now() - Date.parse(at) > keepDays * 86_400_000;
          setLoaded((m) => ({ ...m, [s.fileId]: { src: "", state: e instanceof ApiError && e.status === 404 && old ? "expired" : "error" } }));
        });
    }
    return () => {
      alive = false;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, ids, keepDays]);
  return shots.map((s, i) => ({ id: s.fileId, label: shotLabel(s, i), src: loaded[s.fileId]?.src ?? "", state: loaded[s.fileId]?.state ?? "loading" }));
}

const shotLabel = (s: Screenshot, i: number) => {
  const src = s.source ?? "";
  const label = src.includes("verified") ? "게시글 확인" : src.includes("posted") ? "게시 직후 화면" : src.includes("deleted") ? "삭제 후 화면" : s.name;
  return `${String(i + 1).padStart(2, "0")} · ${label}`;
};

export function PublicationEvidence({ p }: { p: Publication }) {
  const verdict = verdictOf(p);
  const succeeded = p.taskStatus === "succeeded";
  const shots = p.screenshots ?? [];
  const captures = useCaptureAssets(p.executor.customerId, shots, p.postedAt ?? p.posted?.postedAt ?? null);
  const beforePost = (p.stage === "review" || p.stage === "run" || !p.stage) && !p.posted && !p.postedAt;
  return (
    <div className="lwp-evidence">
      <TenantRoute executor={p.executor} subject={p.subject} crew={p.crew} />
      <PublishProgress stage={p.stage} failure={p.failure} succeeded={succeeded} />
      {p.failure && (
        <div className="lwp-callout" role="note">
          <TriangleAlert size={18} />
          <div>
            <strong>{failureLabels[p.failure]}</strong>
            <p>
              {p.failure === "review_rejected"
                ? "원고를 수정해 새 작업으로 등록하세요. 자동 재시도와 사용량 산입은 하지 않습니다."
                : p.failure === "unknown_result"
                  ? "자동 재배정·재게시가 중단되었습니다. 브릿지가 다시 연결되면 글 목록을 대조하고, 글이 있으면 완료 검토부터 이어갑니다."
                  : p.failure === "needs_human"
                    ? "현장 인증과 게시 여부를 먼저 확인하세요. 자동 재시도하지 않습니다."
                    : "게시글이 남아 있습니다. 캡처를 대조한 뒤 결과 검토함에서 조치하세요."}
            </p>
          </div>
        </div>
      )}
      <div className="lwp-evidence-grid">
        <section className="lwp-evidence-copy">
          <h3>
            <ShieldCheck size={17} /> {beforePost ? "원고 검토" : "AI 완료 판정"} <Badge tone={verdictTone(verdict)}>{verdict}</Badge>
          </h3>
          {p.verification?.reasons.length ? (
            <ul className="lwp-reasons">
              {p.verification.reasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          ) : p.review?.reasons.length && beforePost ? (
            <ul className="lwp-reasons">
              {p.review.reasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          ) : (
            <p>{p.summary ?? (beforePost ? "게시 전입니다." : "판정 사유가 없습니다.")}</p>
          )}
          <dl>
            <div>
              <dt>글 제목</dt>
              <dd>{p.postTitle ?? p.title}</dd>
            </div>
            <div>
              <dt>게시 시각</dt>
              <dd>{p.postedAt ? fmtDateTime(p.postedAt) : p.posted?.postedAt ? fmtDateTime(p.posted.postedAt) : "게시 전"}</dd>
            </div>
            <div>
              <dt>실행 위치</dt>
              <dd>
                {p.machineLabel ?? "머신 미정"}
                {p.executor.blogId ? ` · ${p.executor.blogId}` : p.deviceSerial ? ` · ${p.deviceSerial}` : ""}
              </dd>
            </div>
            <div>
              <dt>관리자 검토</dt>
              <dd>
                <Badge>{p.resultReview ? reviewLabels[p.resultReview.status] : beforePost ? "완료 검토 전" : "검토 대상 아님"}</Badge>
              </dd>
            </div>
          </dl>
          {p.posted?.url ? (
            <a className="lwp-link" href={p.posted.url} target="_blank" rel="noreferrer">
              게시글 열기 <ExternalLink size={14} />
            </a>
          ) : (
            <p className="meta-crew-muted">{beforePost ? "게시 전 · 결과 URL 없음" : "결과 URL 미확보 · 게시 여부 확인 필요"}</p>
          )}
          {p.review && (
            <details>
              <summary>
                원고 검토 결과 · {p.review.verdict === "pass" ? "통과" : p.review.verdict === "warn" ? "경고" : "반려"}
                {p.review.reasons.length ? ` · ${p.review.reasons.length}건` : ""}
              </summary>
              {p.review.reasons.length ? (
                <ul className="lwp-reasons">
                  {p.review.reasons.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              ) : (
                <p>대상 고객사의 상호·지역·업무 분야와 원고를 대조했고, 삽입된 지시문과 첨부 자리 표시를 검사했습니다.</p>
              )}
              <p className="meta-crew-muted">테넌트 소개가 비어 있으면 상호·지역으로만 판단하고 ‘근거 부족’ 경고가 남습니다.</p>
            </details>
          )}
        </section>
        <section>
          <h3>
            증거 캡처 <small>{shots.length}장</small>
          </h3>
          {shots.length ? (
            <EvidenceViewer captures={captures} />
          ) : (
            <div className="lwp-empty">
              <FileSearch />
              <strong>확보된 캡처가 없습니다</strong>
              <p>
                {p.failure === "review_rejected"
                  ? "디바이스를 조작하기 전에 원고 검토에서 중단했습니다."
                  : beforePost
                    ? "완료 검토에서 게시 화면과 첨부를 캡처합니다."
                    : "확인되지 않은 결과를 성공으로 표시하지 않습니다."}
              </p>
            </div>
          )}
          <small className="lwp-caption">관리자 전용 · 실행 고객사 범위 보관 · 보관 기간은 시스템 설정</small>
        </section>
      </div>
    </div>
  );
}

/* ---------- 결과 검토함 ---------- */

const historyLabel = (s: string) => (s === "delete_failed" ? "삭제 실패" : (reviewLabels[s as ReviewState] ?? s));

function ReviewDialog({ taskId, me, onClose }: { taskId: string | null; me: Me; onClose: () => void }) {
  const q = useResultReview(taskId);
  const settings = useSettings();
  const formId = useId();
  const [choice, setChoice] = useState<"kept" | "delete_requested" | "held">("kept");
  const [memo, setMemo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  useEffect(() => {
    setChoice("kept");
    setMemo("");
    setError(null);
    setSaved(null);
  }, [taskId]);
  const invalidate = [["admin", "results"], ["admin", "runs"], ["c"]];
  const act = useAction((v: { action: string; note?: string }) => api.post(`/admin/results/${taskId}/${v.action}`, { note: v.note }), invalidate);
  const row = q.data;
  const p = row ? publicationFromRow(row) : null;
  const canReview = can(me, "TASK_CREATE");
  const editable = !!p && canReview && ["pending", "held"].includes(p.resultReview?.status ?? "");
  const reviewDays = Number(settings.data?.find((s) => s.key === "result_review_days")?.value ?? 7);
  const history = p?.resultReview?.history ?? [];
  const submit = () => {
    if (!p) return;
    if (choice !== "kept" && !memo.trim()) return setError("삭제 요청과 보류에는 사유를 입력해 주세요.");
    if (choice === "delete_requested" && !p.posted?.url) return setError("글 URL을 확보한 뒤 삭제를 요청할 수 있습니다. 지금은 보류로 기록해 주세요.");
    setError(null);
    act
      .mutateAsync({ action: choice === "kept" ? "keep" : choice === "held" ? "hold" : "delete-request", note: memo.trim() || undefined })
      .then(() => {
        setSaved(choice === "delete_requested" ? "삭제 작업을 요청했습니다. 완료 보고가 오면 삭제됨으로 바뀝니다." : choice === "held" ? "보류로 기록했습니다." : "유지로 확정했습니다.");
        setMemo("");
      })
      .catch((e) => setError(errorMessage(e)));
  };
  return (
    <Dialog
      open={!!taskId}
      onOpenChange={(v) => !v && onClose()}
      title={p?.postTitle ?? p?.title ?? "결과 검토"}
      description={p ? `${p.taskId.slice(0, 8)} · 관리자 전용 결과 검토` : ""}
      size="detail"
      footer={
        <DialogActions
          secondary={{ label: "닫기", onClick: onClose }}
          primary={editable ? { label: choice === "delete_requested" ? "삭제 작업 요청" : choice === "held" ? "보류 저장" : "유지로 확정", tone: choice === "delete_requested" ? "danger" : "default", type: "submit", form: formId, disabled: act.isPending } : undefined}
          note={error ?? saved ?? (editable ? "조치와 메모는 검토 이력에 남습니다." : "결과와 검토 기록을 확인합니다.")}
        />
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorBox error={errorMessage(q.error)} />
      ) : p ? (
        <>
          <PublicationEvidence p={p} />
          <section className="lwp-review-history">
            <h3>검토 이력</h3>
            {history.length ? (
              <ol>
                {history.map((h, i) => (
                  <li key={i}>
                    {historyLabel(h.status)} · {h.note ?? "메모 없음"} · {h.byName ?? (h.by === null ? "시스템" : "관리자")} {fmtDateTime(h.at)}
                  </li>
                ))}
              </ol>
            ) : (
              <p>
                아직 관리자 검토가 없습니다.
                {p.resultReview?.status === "pending" && p.resultReview.openedAt ? ` 자동 유지 예정 ${fmtDateTime(new Date(Date.parse(p.resultReview.openedAt) + reviewDays * 86_400_000).toISOString())}` : ""}
              </p>
            )}
            {p.resultReview?.status === "delete_requested" && (
              <div className="lwp-callout">
                <Clock3 size={18} />
                <div>
                  <strong>삭제 작업 대기 · {p.resultReview.deleteTaskId?.slice(0, 8) ?? ""}</strong>
                  <p>
                    {p.machineLabel ?? "같은 머신"}
                    {p.executor.blogId ? ` · ${p.executor.blogId}` : ""} · 같은 머신과 계정에 배정됩니다. 완료 보고를 받기 전까지 삭제됨으로 바뀌지 않습니다.
                  </p>
                </div>
              </div>
            )}
            {p.resultReview?.deleteError && (
              <div className="lwp-callout">
                <TriangleAlert size={18} />
                <div>
                  <strong>삭제 작업 실패</strong>
                  <p>{p.resultReview.deleteError} · 다시 요청하거나 보류로 두세요.</p>
                </div>
              </div>
            )}
            {p.resultReview?.status === "deleted" && <Badge tone="teal">삭제 완료 보고 수신 · {fmtDateTime(p.resultReview.deletedAt ?? null)}</Badge>}
          </section>
          {editable && (
            <form
              id={formId}
              className="mcc-form lwp-review-form"
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
            >
              <h3>검토 조치</h3>
              <Select
                label="조치 선택"
                value={choice}
                onChange={(e) => {
                  setChoice(e.target.value as typeof choice);
                  setError(null);
                }}
              >
                <option value="kept">유지</option>
                <option value="delete_requested" disabled={!p.posted?.url}>
                  삭제 요청{!p.posted?.url ? " · URL 확보 필요" : ""}
                </option>
                <option value="held">보류</option>
              </Select>
              <label className="field">
                검토 메모{choice !== "kept" ? " (필수)" : " (선택)"}
                <textarea className="input" value={memo} onChange={(e) => setMemo(e.target.value)} rows={3} placeholder="대조한 내용과 조치 사유를 기록하세요" />
              </label>
              {choice === "delete_requested" && <p className="lwp-warning">원본 게시글을 삭제하는 별도 작업을 요청합니다. 대상 URL과 실행 계정을 확인해 주세요.</p>}
            </form>
          )}
        </>
      ) : null}
    </Dialog>
  );
}

/** 결과 검토함 화면. 전체 관리(모든 고객사) 또는 고객사 범위(직원이 고객사를 연 경우). */
export function ResultReviewScreen({ me, customerId }: { me: Me; customerId?: string }) {
  const q = useResultReviews({ customerId, status: "all", limit: 200 });
  const customers = useCustomers("");
  const focus = useReviewFocus((s) => s.taskId);
  const setFocus = useReviewFocus((s) => s.focus);
  const [selected, setSelected] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [execution, setExecution] = useState("all");
  const [target, setTarget] = useState("all");
  const [verdict, setVerdict] = useState("all");
  useEffect(() => {
    if (focus) {
      setSelected(focus);
      setFocus(null);
    }
  }, [focus, setFocus]);
  const rows = (q.data?.items ?? []).map(publicationFromRow);
  const visible = rows.filter(
    (r) =>
      (status === "all" || r.resultReview?.status === status) &&
      (execution === "all" || r.executor.customerId === execution) &&
      (target === "all" || r.subject.customerId === target) &&
      (verdict === "all" || verdictOf(r) === verdict) &&
      `${r.title} ${r.postTitle ?? ""} ${r.taskId}`.toLowerCase().includes(query.toLowerCase()),
  );
  const count = (f: (r: Publication) => boolean) => String(rows.filter(f).length);
  const customerOptions = (customers.data?.items ?? []).map((c) => ({ id: c.id, name: c.name }));
  return (
    <>
      {/* 카드를 누르면 아래 목록의 검토 상태 필터를 맞춘다 (2026-10-21). 확인 불가·불일치는 한 필터로 거를 수 없어 숫자만 */}
      <MetricGrid
        items={[
          { label: "검토 대기", value: count((r) => r.resultReview?.status === "pending"), icon: <ClipboardCheck />, tone: "amber", onOpen: () => setStatus("pending"), openLabel: "검토 대기만 보기" },
          { label: "확인 불가·불일치", value: count((r) => ["pending", "held"].includes(r.resultReview?.status ?? "") && verdictOf(r) !== "일치"), icon: <TriangleAlert />, tone: "coral" },
          { label: "보류", value: count((r) => r.resultReview?.status === "held"), icon: <Clock3 />, onOpen: () => setStatus("held"), openLabel: "보류만 보기" },
          { label: "삭제 요청", value: count((r) => r.resultReview?.status === "delete_requested"), icon: <Trash2 />, onOpen: () => setStatus("delete_requested"), openLabel: "삭제 요청만 보기" },
        ]}
      />
      <FilterBar>
        <SearchField showLabel label="결과 검색" placeholder="글 제목 또는 작업 번호" value={query} onChange={(e) => setQuery(e.target.value)} />
        <Select label="검토 상태" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">전체 상태</option>
          {Object.entries(reviewLabels).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
        {!customerId && (
          <Select label="실행 고객사" value={execution} onChange={(e) => setExecution(e.target.value)}>
            <option value="all">전체 고객사</option>
            {customerOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        )}
        <Select label="대상 고객사" value={target} onChange={(e) => setTarget(e.target.value)}>
          <option value="all">전체 고객사</option>
          {customerOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select label="AI 판정" value={verdict} onChange={(e) => setVerdict(e.target.value)}>
          <option value="all">전체 판정</option>
          {["일치", "불일치", "확인 불가"].map((v) => (
            <option key={v}>{v}</option>
          ))}
        </Select>
      </FilterBar>
      <div className="lwp-list-note">
        <span>{visible.length}개 결과 · 게시 후 검토</span>
        <small>미검토 결과는 설정한 일수 뒤 유지로 전환 · 보류는 별도 관리</small>
      </div>
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorBox error={errorMessage(q.error)} onRetry={() => void q.refetch()} />
      ) : visible.length === 0 ? (
        <div className="lwp-empty">
          <FileSearch />
          <strong>{rows.length ? "조건에 맞는 결과가 없습니다" : "검토할 결과가 없습니다"}</strong>
          {rows.length > 0 && (
            <Button
              variant="secondary"
              onClick={() => {
                setQuery("");
                setStatus("all");
                setExecution("all");
                setTarget("all");
                setVerdict("all");
              }}
            >
              필터 초기화
            </Button>
          )}
        </div>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>게시글 / 작업</th>
              <th>실행 → 대상 고객사</th>
              <th>게시 시각</th>
              <th>AI 판정</th>
              <th>캡처</th>
              <th>검토 상태</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => {
              const v = verdictOf(r);
              return (
                <tr key={r.taskId}>
                  <td>
                    <button className="meta-crew-text-button" onClick={() => setSelected(r.taskId)}>
                      {r.postTitle ?? r.title}
                    </button>
                    <small className="meta-crew-sub">{r.taskId.slice(0, 8)}</small>
                  </td>
                  <td>
                    {r.executor.name} → {r.subject.name}
                    {r.crew && (
                      <small className="meta-crew-sub">
                        <Badge tone="blue">크루 작업</Badge>
                      </small>
                    )}
                  </td>
                  <td>{r.postedAt ? fmtDateTime(r.postedAt) : "게시 전"}</td>
                  <td>
                    <Badge tone={verdictTone(v)}>{v}</Badge>
                    {r.failure && <small className="meta-crew-sub">{failureLabels[r.failure]}</small>}
                  </td>
                  <td>
                    {r.screenshots?.length ? (
                      <button type="button" className="lwp-thumb" onClick={() => setSelected(r.taskId)} aria-label={`${r.title} 캡처 ${r.screenshots.length}장 보기`}>
                        <ScreenshotImage customerId={r.executor.customerId} shot={r.screenshots[0]!} />
                      </button>
                    ) : (
                      <span className="meta-crew-muted">미확보</span>
                    )}
                  </td>
                  <td>{r.resultReview && <Badge tone={reviewTone(r.resultReview.status)}>{reviewLabels[r.resultReview.status]}</Badge>}</td>
                  <td>
                    <Button size="sm" variant="ghost" onClick={() => setSelected(r.taskId)}>
                      검토 상세
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
      <ReviewDialog taskId={selected} me={me} onClose={() => setSelected(null)} />
    </>
  );
}

/* ---------- 고객사 설정: 테넌트 소개 ---------- */

export function TenantIntroductionPanel({ customer, canEdit }: { customer: CustomerSummary; canEdit: boolean }) {
  const intro = ((customer.settings ?? {}) as { intro?: TenantIntro }).intro ?? {};
  const [msg, setMsg] = useState<string | null>(null);
  const save = useAction((v: TenantIntro) => api.patch(`/admin/customers/${customer.id}`, { settings: { intro: v } }), [keys.summary(customer.id), keys.customerDetail(customer.id)]);
  return (
    <Panel title="테넌트 소개" extra={<Badge>관리자 전용</Badge>}>
      <form
        className="mcc-form mcc-padding"
        onSubmit={(e) => {
          e.preventDefault();
          const d = new FormData(e.currentTarget);
          const v: TenantIntro = {};
          for (const k of ["bizName", "region", "specialty", "blogId"] as const) {
            const s = String(d.get(k) ?? "").trim();
            if (s) v[k] = s;
          }
          save
            .mutateAsync(v)
            .then(() => setMsg("테넌트 소개를 저장했습니다."))
            .catch((er) => setMsg(errorMessage(er)));
        }}
      >
        <p className="meta-crew-muted">원고가 대상 고객사의 내용인지 판단하는 근거입니다. 브릿지 AI가 실행 고객사와 대상 고객사의 소개를 각각 대조합니다.</p>
        <div className="lwp-form-grid">
          <Field name="bizName" label="상호" defaultValue={intro.bizName ?? customer.name} required readOnly={!canEdit} />
          <Field name="region" label="지역" defaultValue={intro.region ?? ""} placeholder="예: 서울 강남구" readOnly={!canEdit} />
          <Field name="specialty" label="진료·업무 분야" defaultValue={intro.specialty ?? ""} placeholder="진료 분야 또는 주요 서비스" readOnly={!canEdit} />
          <Field name="blogId" label="블로그 계정 ID" defaultValue={intro.blogId ?? ""} placeholder="실행 계정 대조에 사용할 ID" readOnly={!canEdit} />
        </div>
        <p className="lwp-caption">소개가 부족하면 상호·지역으로 검토하며 결과에 ‘근거 부족’ 경고가 남습니다.</p>
        {canEdit && (
          <div className="lwp-save">
            <Button type="submit" disabled={save.isPending}>
              소개 저장
            </Button>
            {msg && <span role="status">{msg}</span>}
          </div>
        )}
      </form>
    </Panel>
  );
}

/* ---------- 시스템 설정: 게시 결과 검토 정책 ---------- */

export function ReviewPolicyPanel({ me }: { me: Me }) {
  const q = useSettings();
  const [msg, setMsg] = useState<string | null>(null);
  const save = useAction((v: { key: string; value: unknown }) => api.put(`/admin/settings/${v.key}`, { value: v.value }), [keys.settings]);
  const editable = can(me, "BILLING_MANAGE");
  const get = (k: string) => q.data?.find((s) => s.key === k)?.value;
  return (
    <Panel title="게시 결과 검토 정책">
      <form
        className="mcc-form mcc-padding"
        onSubmit={(e) => {
          e.preventDefault();
          const d = new FormData(e.currentTarget);
          Promise.all([
            save.mutateAsync({ key: "result_review_days", value: Number(d.get("review")) }),
            save.mutateAsync({ key: "result_screenshot_days", value: Number(d.get("shots")) }),
          ])
            .then(() => setMsg("정책을 저장했습니다. 30초 안에 반영됩니다."))
            .catch((er) => setMsg(errorMessage(er)));
        }}
      >
        <div className="lwp-form-grid">
          <Field name="review" label="미검토 결과 자동 유지 (일)" type="number" min={1} max={90} defaultValue={Number(get("result_review_days") ?? 7)} required readOnly={!editable} />
          <Field name="shots" label="증거 캡처 보관 (일)" type="number" min={7} max={730} defaultValue={Number(get("result_screenshot_days") ?? 90)} required readOnly={!editable} />
        </div>
        <p className="meta-crew-muted">미검토 결과의 자동 종료와 캡처 보관 기간입니다. 새벽 정리 작업이 적용하고, 관리자 검토 조치와 설정 변경은 감사 기록에 남습니다.</p>
        {editable && (
          <div className="lwp-save">
            <Button type="submit" disabled={save.isPending || q.isLoading}>
              정책 저장
            </Button>
            {msg && <span role="status">{msg}</span>}
          </div>
        )}
      </form>
    </Panel>
  );
}

/** 실행 이력 표의 상태 필터: 실행 상태와 게시 실패 분류를 한 목록으로. */
export function reviewStatusLabel(errorKind: string | null | undefined): string | null {
  if (!errorKind) return null;
  return failureLabels[errorKind] ?? errorKindNames[errorKind] ?? null;
}

export const fmtPosted = (s: string | null | undefined) => (s ? fmtTime(s) : "게시 전");
